import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { DateProvider } from '@mimir/common/util/dateProvider';
import { doesPathExist } from '@mimir/common/util/fs';
import type { Logger } from '@mimir/common/types/logger';
import type { DownloadReporter } from '../model/ongoingDownload';

const FILE_WAIT_DEADLINE_MS = 60 * 1000;
const FILE_CHECK_INTERVAL_MS = 1000;

/**
 * Waits for a file to become available at any of the candidate paths, up to one minute.
 *
 * This should be called after the server and storage manager have finished their work,
 * but we cannot assume the file is immediately visible on the local file system at that
 * point. The file system may not be atomic: even if Kelda or the storage manager believes
 * the file has been delivered, its appearance locally may be delayed (e.g. due to slow
 * mounts or network file systems).
 *
 * Keeps reporters informed via onActivity. Throws if the file does not appear within
 * the deadline.
 */
export async function waitForLocallyAvailableFile(
  applicationContext: AdobePanelContext | ElectronContext,
  logger: Logger,
  dateProvider: DateProvider,
  candidatePaths: string[],
  reporters: DownloadReporter[],
): Promise<string> {
  const deadline = dateProvider.now().getTime() + FILE_WAIT_DEADLINE_MS;
  do {
    const before = dateProvider.now().getTime();
    for (const path of candidatePaths) {
      const exists = await doesPathExist(applicationContext.nodeApi, logger, path, false);
      if (exists) {
        return path;
      }
    }
    const after = dateProvider.now().getTime();

    let msg = 'Waiting for filesystem to make downloaded file available.';
    if (after - before > 1000) {
      msg += ' File system is slow to respond.';
    }
    for (const reporter of reporters) {
      reporter.onActivity(msg);
    }
    await asyncTimeout(FILE_CHECK_INTERVAL_MS);
  } while (dateProvider.now().getTime() < deadline);

  const pathDesc = [...candidatePaths].join(' or ');
  throw new Error('File did not arrive at ' + pathDesc + ' within one minute of transfer ending.');
}
